/**
 * 表セクションの列の更新。
 * templateNodes を入力に、列の追加/削除/並び替えを行う純粋関数。
 */

import * as Sentry from '@sentry/browser'
import {
  TemplateNodeSchema,
  TemplateQuestion,
} from '@ulysses-inc/harami_api_client'
import type { TemplateNodesDict } from 'src/exShared/types/types'
import { getCreateNodeId } from 'src/features/templateEdit/common/createNode'
import type { ColumnInputType } from './columnType'
import {
  collectSubtreeNodeIds,
  getRowCellQuestions,
  getTableColumnNodes,
  getTableRowSectionNodes,
  getTableTemplateRowNode,
} from './selector'
import {
  buildNewQuestionNode,
  QUESTION_FROM_RESPONSE_TYPE,
} from './tableNodeBuilders'
import type { TableSectionUpdate } from './tableSection'

// --- 列操作 ---

/**
 * 表に列を1列追加する。表直下に列ノードは作らず、全行(固定行＋テンプレート行)の
 * `insertIndex` の位置に質問を1つずつ挿入する(末尾に追加するなら列数を渡す)。
 * テンプレート行に追加した質問の id を activeNodeId(=選択される列)として返す。
 */
export const addTableColumn = (
  tableNode: TemplateNodeSchema,
  templateNodes: TemplateNodesDict,
  responseType: ColumnInputType,
  insertIndex: number,
): TableSectionUpdate & { activeNodeId: number } => {
  const question = QUESTION_FROM_RESPONSE_TYPE[responseType]
  const { templateNodes: nextNodes, columnQuestionId } =
    insertColumnCellsIntoRows(tableNode, templateNodes, insertIndex, question)

  return { templateNodes: nextNodes, activeNodeId: columnQuestionId }
}

/**
 * 全行(固定行＋テンプレート行)の insertIndex 位置に、新規セル質問を1つずつ挿入する。
 * 返り値の columnQuestionId は、テンプレート行に挿入した質問の id。
 */
const insertColumnCellsIntoRows = (
  tableNode: TemplateNodeSchema,
  templateNodes: TemplateNodesDict,
  insertIndex: number,
  question: TemplateQuestion,
): { templateNodes: TemplateNodesDict; columnQuestionId: number } => {
  const templateRowId = getTableTemplateRowNode(tableNode, templateNodes).id

  // id は一度だけ採番起点を取り、ローカルにインクリメントする
  // (getCreateNodeId は全ノード走査なのでループ内では呼ばない)。
  let nextCellId = getCreateNodeId(templateNodes)
  const addedCells: TemplateNodesDict = {}
  const updatedRows: TemplateNodesDict = {}
  let columnQuestionId: number | undefined

  for (const row of getTableRowSectionNodes(tableNode, templateNodes)) {
    const cellId = nextCellId++
    const rowCellIds = [...row.nodes]
    rowCellIds.splice(insertIndex, 0, cellId)
    addedCells[cellId] = buildNewQuestionNode(cellId, { ...question })
    updatedRows[row.id] = { ...row, nodes: rowCellIds }

    // テンプレート行に挿入したセルの id を、選択対象の列として保存する。
    if (row.id === templateRowId) {
      columnQuestionId = cellId
    }
  }

  if (columnQuestionId === undefined) {
    throw new Error('テンプレート行の質問ノードの id が取得できません')
  }

  return {
    templateNodes: { ...templateNodes, ...addedCells, ...updatedRows },
    columnQuestionId,
  }
}

/** 表の列を並び替える。全行のセルも同じ並びに揃える。 */
export const moveTableColumn = (
  tableNode: TemplateNodeSchema,
  templateNodes: TemplateNodesDict,
  fromIndex: number,
  toIndex: number,
): TableSectionUpdate => {
  const columns = getTableColumnNodes(tableNode, templateNodes)

  if (fromIndex >= columns.length || toIndex >= columns.length) {
    Sentry.captureException(
      new Error('範囲外のインデックスが指定されています'),
      {
        extra: { fromIndex, toIndex, columnsLength: columns.length },
      },
    )
    return { templateNodes }
  }
  if (fromIndex === toIndex) {
    return { templateNodes }
  }

  const nodes: TemplateNodesDict = { ...templateNodes }
  for (const row of getTableRowSectionNodes(tableNode, nodes)) {
    const cellIds = getRowCellQuestions(row, nodes).map(c => c.id)
    if (cellIds.length !== columns.length) {
      // 「全行は同一の列構造を持つ」不変条件が崩れている。並び替えを部分適用すると
      // この行だけ列がズレるためスキップするが、原因観測のため記録しておく。
      Sentry.captureException(
        new Error('行のセル数が列数と一致しないため並び替えをスキップしました'),
        {
          extra: {
            rowId: row.id,
            cellCount: cellIds.length,
            columnCount: columns.length,
          },
        },
      )
      continue
    }
    nodes[row.id] = {
      ...row,
      nodes: moveItem(cellIds, fromIndex, toIndex),
    }
  }

  // 表直下は行のみで列ノードは持たないため、table.nodes は変更しない。
  return { templateNodes: nodes }
}

/**
 * 列を削除する。全行(固定行＋テンプレート行)の同じ位置の質問を削除する。
 * columnId は列= テンプレート行の質問 の id。表直下は行のみなので table.nodes は変更しない。
 */
export const deleteTableColumn = (
  tableNode: TemplateNodeSchema,
  templateNodes: TemplateNodesDict,
  columnId: number,
): TableSectionUpdate => {
  const columns = getTableColumnNodes(tableNode, templateNodes)
  const columnIndex = columns.findIndex(c => c.id === columnId)
  if (columnIndex < 0) {
    return { templateNodes }
  }

  const nodes = { ...templateNodes }
  const idsToRemove = new Set<number>()

  for (const row of getTableRowSectionNodes(tableNode, nodes)) {
    const cell = getRowCellQuestions(row, nodes)[columnIndex]
    if (cell) {
      // セルは子に 条件分岐→分岐質問を持つのでサブツリーごと削除する。
      collectSubtreeNodeIds(cell.id, nodes).forEach(id => idsToRemove.add(id))
      nodes[row.id] = {
        ...row,
        nodes: row.nodes.filter(id => id !== cell.id),
      }
    }
  }

  const nextNodes: TemplateNodesDict = {}
  for (const [id, node] of Object.entries(nodes)) {
    if (!idsToRemove.has(Number(id))) {
      nextNodes[Number(id)] = node
    }
  }

  // 削除後は隣接列(同位置→無ければ左隣)を選択状態にする。列が無くなれば表自身を選ぶ。
  const remainingColumns = getTableColumnNodes(tableNode, nextNodes)
  const activeNodeId =
    remainingColumns[Math.min(columnIndex, remainingColumns.length - 1)]?.id ??
    tableNode.id

  return { templateNodes: nextNodes, activeNodeId }
}

/** 配列内の1要素を fromIndex から toIndex へ移動する(列・セル並び替えの共通処理)。 */
const moveItem = <T>(items: T[], fromIndex: number, toIndex: number): T[] => {
  const next = [...items]
  const [moved] = next.splice(fromIndex, 1)
  if (moved === undefined) {
    return next
  }
  next.splice(toIndex, 0, moved)
  return next
}
